import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from "@mui/material/Box"
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isEqual, size, replace, cloneDeep, isUndefined, isNull, isNil } from "lodash";

import locationItemService from "@plannel/services/data/location-item-service";
import locationService from "@plannel/services/data/location-service"
import CycleCalendarRenderer from "@plannel/components/aggrid/CycleCalendarRenderer";
import PaginationContainer from "@plannel/components/PaginationContainer";
import RpBulkValueUpdateModal from "@plannel/pages/replenishment-plan/rp-modal/RpBulkValueUpdateModal";
import Dialog from "@plannel/components/Dialog";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import GridDatePicker from "@plannel/components/GridDatePicker";
import { AddButton, BulkValueUpdateButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import CodeUtil from "../utils/code-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import NumberUtil from "@plannel/utils/number-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { P_SYSTEM, Q_SYSTEM, MIN_MAX_SYSTEM, BASE_STOCK_SYSTEM } = CodeUtil.rpPolicyCd;
const RP_POLICY_LIST = [P_SYSTEM, Q_SYSTEM, MIN_MAX_SYSTEM, BASE_STOCK_SYSTEM];
const INV_CLASS_CD_LIST = CodeUtil.dpABCConfig;
const itemType = CodeUtil.itemType;
const { snackBarMessageColor } = ColorUtil;
const ALT_ROUTE_POLICY_OPTIONS = ["priority", "proportion"]

const LocationItem = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? null);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [locationItem, setLocationItem] = useState([]);
  const [locations, setLocations] = useState([]);

  const updateTargets = ["moq", "ioq", "cycleInvFlg", "opInvDays", "targetSupplyDays", "orderMethod", "orderCalendar", /*"demandCalendar",*/ "otdLtD", "ipFixedFlg", "ipApplied", "mpApplied", "rpApplied", "activeFlg"];
  const [massUpdateModalOpen, setMassUpdateModalOpen] = useState(false);

  const currencyMap = useRef({});
  const [csvColDef, setCsvColDef] = useState([]);
  const EXCEL_EXPORT = "excelExport";
  const INV_HOLD_COST_RATIO = "invHoldCostRatio"
  const MC_FLG = "mcFlg";
  const MC_FENCE_FLG = "mcFenceFlg";
  const userData = JSON.parse(localStorage.getItem("user"));

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locationCdValueFormatter = (location) => {
    const formatter = location?.value?.locCd;
    return formatter ?? "";
  }

  const locationNmValueFormatter = (location) => {
    const formatter = location?.value?.name;
    return formatter ?? "";
  }

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    
    return nullSafeComparator(val1, val2, isDescending);
  }


  const dynamicEditable = params => {
    const { rpPolicyCd } = params.data;
    const { colId } = params.column.getColDef();

    if (isEqual(colId, "replenishCalendar")) {
      if (isEqual(rpPolicyCd, Q_SYSTEM) || isEqual(rpPolicyCd, BASE_STOCK_SYSTEM)) {
        return false;
      }
    }

    return true;
  };

  const currencyRenderer = (param, isExport = false) => {
    const locCd = param?.data?.location?.locCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      }
    }
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return null;
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      if (param?.colDef?.field === INV_HOLD_COST_RATIO) {
        return NumberUtil.formatNumber(value, { maximumFractionDigits: 4, minimumFractionDigits: 4 }).replace(/,/g, "");
      }
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const altRoutePolicyValueFormatter = (params) => {
    if (isEmpty(params?.value)) {
      return "";
    }
    const { value } = params;

    return t(value);
  }

  const mcFenceEditable = params => isEqual(params?.data?.[MC_FLG], true);

  const mcFenceFlgRenderer = params => {
    if (params.node.rowPinned) {
      return null;
    }

    const editable = mcFenceEditable(params);

    return (
      <input
        type="checkbox"
        defaultChecked={params.value}
        disabled={!editable}
        onClick={e => {
          if (!editable) {
            e.preventDefault();
            return;
          }
          params.node.setDataValue(params.column, !params.value);
        }}
      />
    );
  };

  const columnDefs = [
    {
      headerName: "locCd", field: "location", colId: "locCd",
      type: ['nonEditableColumn'],
      valueFormatter: locationCdValueFormatter,
      cellClass: 'stringType',
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "locCd", isDescending),
      width: 150, filterType: 'string',
    },
    {
      headerName: "locName", field: "location", colId: "locNm",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: locationNmValueFormatter,
      cellEditorParams: {
        values: locations
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 150, filterType: 'string'
    },
    { headerName: "itemCd", field: "itemCd", width: 150, cellClass: 'stringType', filterType: 'string' },
    { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 280, filterType: 'string' },
    { headerName: "itemType", field: "itemType", type: ["nonEditableColumn"], width: 280, hide: true, filterType: 'string', filterOption: Object.keys(itemType).map(key => key.toLowerCase())},
    { headerName: "shipRelAprvlDt", field: "shipRelAprvlDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 150, filterType: 'date', hide: true, valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "readyToSellinDt", field: "readyToSellinDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 135, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "endOfProdDt", field: "endOfProdDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 140, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "endOfSalesDt", field: "endOfSalesDt", type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" }, width: 130, filterType: 'date', valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd")},
    { headerName: "stdSalesPrice", field: "stdUnitPrice", type: ["decimalPointColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 100, filterType: 'number' },
    { headerName: "salesUnitCost", field: "salesUnitCost", type: ["decimalPointColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 100, filterType: 'number' },
    { headerName: "invUnitCost", field: "invUnitCost", type: ["decimalPointColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 100, filterType: 'number' },
    { headerName: "moq", field: "moq", colId: "moq", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "ioq", field: "ioq", colId: "ioq", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "rop", field: "rop", colId: "rop", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "eoq", field: "eoq", type: ["rationalColumn", "rightAligned"], width: 100, filterType: 'number' },
    { headerName: "cycleStockApplied", field: "cycleInvFlg", type: ["booleanColumn"], width: 140, filterType: 'boolean' },
    { headerName: "cycleInvQty", field: "cycleInvQty", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "safetyStockQty", field: "safetyStockQty", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "maxQty", field: "maxQty", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "pushFlg", field: "pushFlg", type: ["booleanColumn"], width: 130, filterType: 'boolean' },
    { headerName: "opInvDays", field: "opInvDays", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "targetSupplyDays", field: "targetSupplyDays", type: ["numberColumn", "rightAligned"], width: 120, filterType: 'number' },
    { headerName: "maxEarlyProdDays", field: "maxEarlyProdDays", type: ["numberColumn", "rightAligned"], width: 140, filterType: 'number' },
    {
      headerName: "invClassCd", field: "invClassCd", minWidth: 60, width: 120,
      cellRenderer: AbcXyzChip,
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: {
        values: INV_CLASS_CD_LIST
      },
      filterType: 'string',
      filterOption: INV_CLASS_CD_LIST
    },
    {
      headerName: "orderMethod", colId: "rpPolicyCd", field: "rpPolicyCd", editable: dynamicEditable,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: params => GridUtils.gridValueL10N(params.value),
      cellEditorParams: {
        values: RP_POLICY_LIST
      },
      width: 130,
      filterType: 'string',
      filterOption: RP_POLICY_LIST
    },
    {
      headerName: "orderCalendar", colId: "replenishCalendar", field: "replenishCalendar", type: "nonEditableColumn",
      valueGetter: params => {
        const { data } = params;
        const value = data?.replenishCalendar;
        return value?.name ? value.name : t("notConfigured");
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      cellRenderer: CycleCalendarRenderer, cellRendererParams: { setSnackInfo, editable: dynamicEditable },
      width: 140,
      filterType: 'string'
    },
    {
      headerName: "demandCalendar", colId: "demandCalendar", field: "demandCalendar", type: "nonEditableColumn", hide: true,
      valueGetter: params => {
        const { data } = params;
        const value = data?.demandCalendar;
        return value?.name ? value.name : t("notConfigured");
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      cellRenderer: CycleCalendarRenderer, cellRendererParams: { setSnackInfo },
      width: 140,
      filterType: 'string'
    },
    {
      headerName: "altRoutePolicyCd", field: "altRoutePolicyCd", width: 150,
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: altRoutePolicyValueFormatter,
      cellEditorParams: {
        valueFormatter: altRoutePolicyValueFormatter,
        values: ALT_ROUTE_POLICY_OPTIONS
      },
      cellClass: 'stringType',
      filterType: "string" ,
      filterOption: ALT_ROUTE_POLICY_OPTIONS
    },
    { headerName: "otdLtD", colId: "otdLtDays", field: "otdLtDays", type: ["rationalColumn", "rightAligned"], width: 100, editable: dynamicEditable, filterType: 'number' },
    { headerName: "orderCost", field: "orderCost", type: ["decimalPointColumn", "rightAligned"], valueFormatter: currencyRenderer, width: 100, filterType: 'number' },
    { headerName: "invHoldCostRatio", field: "invHoldCostRatio", type: ["percentColumn", "rightAligned"], width: 140, filterType: 'percent' },
    { headerName: "mcFlg", field: "mcFlg", type: ["booleanColumn"], width: 110, filterType: 'boolean' },
    {
      headerName: "mcFenceFlg", field: "mcFenceFlg", type: ["booleanColumn"], width: 110, filterType: 'boolean',
      editable: mcFenceEditable, cellRenderer: mcFenceFlgRenderer
    },
    { headerName: "ipFixedFlg", field: "ipFixedFlg", type: ["booleanColumn"], width: 110, filterType: 'boolean' },
    { headerName: "ipApplied", field: "ipFlg", type: ["booleanColumn"], width: 110, filterType: 'boolean' },
    { headerName: "rpApplied", field: "rpFlg", type: ["booleanColumn"], width: 110, filterType: 'boolean' },
    { headerName: "mpApplied", field: "mpFlg", type: ["booleanColumn"], width: 110, filterType: 'boolean' },
    { headerName: "roFlg", field: "roFlg", type: ["booleanColumn"], width: 135, filterType: 'boolean' },
    { headerName: "kittingFlg", field: "kittingFlg", type: ["booleanColumn"], width: 120, filterType: 'boolean' },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 100, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (locationSearch) {
      searchFilters.locCd = locationSearch.locCd;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveLocationItem = () => {
    const params = getRequestParams();
    locationItemService.getAll(params)
      .then(response => {
        const { results, totalPages, currency } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setLocationItem(results);
        setTotalPages(totalPages);

        if (size(currency)) {
          currencyMap.current = currency;
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { locationSearch, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  }

  const retrieveLocation = () => {
    locationService.getLookup({ isLocationMaster: true })
      .then(response => {
        const { results } = response.data;
        if (!isEmpty(results)) {
          setLocations(results);
        } else {
          setLocations([]);
        }
      })
      .catch(e => {
        debug(e);
      })
  }

  const onGridReady = params => {
    retrieveLocation();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    setCsvColDef(cloneDeep(columnDefs))
  }

  // mcFlg 가 바뀌면 종속 컬럼 mcFenceFlg 의 활성/비활성 표시를 다시 그린다.
  // mcFlg 를 끈 경우에는 남아 있던 mcFenceFlg 선택도 함께 해제한다.
  const handleCellValueChanged = params => {
    defaultGridMemo.onCellValueChanged(params);

    if (!isEqual(params.colDef.field, MC_FLG)) {
      return;
    }

    if (!params.newValue && params.data?.[MC_FENCE_FLG]) {
      params.node.setDataValue(MC_FENCE_FLG, false);
    }

    params.api.refreshCells({ rowNodes: [params.node], columns: [MC_FENCE_FLG], force: true });
  };

  const insertRow = () => {
    const newRow = {
      id: null,
      location: null,
      itemCd: null,
      itemName: null,
      shipRelAprvlDt: null,
      readyToSellinDt: null,
      endOfSalesDt: null,
      endOfSalesTs: null,
      stdUnitPrice: null,
      salesUnitCost: null,
      invUnitCost: null,
      altRoutePolicyCd: ALT_ROUTE_POLICY_OPTIONS[0],
      moq: null,
      rop: null,
      cycleInvFlg: true,
      opInvDays: null,
      targetSupplyDays: null,
      maxEarlyProdDays: null,
      safetyStockQty: null,
      maxQty: null,
      pushFlg: false,
      ipFixedFlg: false,
      ipFlg: true,
      rpFlg: true,
      mpFlg: true,
      roFlg: true,
      kittingFlg: true,
      activeFlg: true,
      verNum: null
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const removeLocationItem = () => {
    const locationItemIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        locationItemIds.push(data.id)
      }
    );

    if (locationItemIds.length > 0) {
      locationItemService.remove(locationItemIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        })
        .then(() => {
          retrieveLocationItem();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveLocationItem = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    // rop 는 safetyStockQty 보다 클 수 없다 (둘 다 입력된 행만 검사)
    const hasInvalidRop = allStateData.some(({ rop, safetyStockQty }) =>
      !isNil(rop) && rop !== "" && !isNil(safetyStockQty) && safetyStockQty !== "" && Number(rop) > Number(safetyStockQty)
    );
    if (hasInvalidRop) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_RopCannotExceedSafetyStockQty") });
      return;
    }

    if (allStateData.length > 0) {
      locationItemService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveLocationItem();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveLocationItem();
    } else if (action === "DELETE") {
      removeLocationItem();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveLocationItem();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveLocationItem();
    }

    return () => {
      setLocationItem([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setLocationItem([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearch, advancedFilters]);

  useEffect(() => {
    if (!isEmpty(locations)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);


  return (
    <>
      <FilterContainer>
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch}
          onChange={(_, newValue) => setLocationSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ minWidth: 240, mr: 1 }}
          renderInput={(params) => (
            <TextField {...params} label={t("location")} />
          )}
        />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={locationItemService} params={getRequestParams()} columnDefs={csvColDef.filter(columnDef => !columnDef.hide)} />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <BulkValueUpdateButton onClick={() => setMassUpdateModalOpen(true)} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={locationItem}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onCellValueChanged={handleCellValueChanged}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <RpBulkValueUpdateModal
        t={t}
        updateTargets={updateTargets}
        service={locationItemService}
        modalOpen={massUpdateModalOpen}
        setModalOpen={setMassUpdateModalOpen}
        setSnackInfo={setSnackInfo}
        setProgressOn={setProgressOn}
        retrieve={retrieveLocationItem}
      />
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Backdrop sx={{ color: '#fff', zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
    </>
  );
};

export default withTranslation()(LocationItem);